import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { cn } from "@shared/lib/utils";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Separator } from "@shared/ui/separator";
import { MapPin, Search } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useBillData,
  useBillDraftDetails,
  useBillTemplates,
  useConsumers,
  useSubmitConsumerSelection,
  useUpdateConsumerSelection,
} from "@features/bx/singleBillGenaration/hooks";
import { Consumer } from "../../../bx/singleBillGenaration/types";
import { useDebounce } from "@shared/hooks/useDebounce";
import { Badge } from "@shared/ui/badge";
import { useChoices } from "@features/bx/bill-cycle-v2/hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { normalizeDate } from "@shared/utils/dateUtils";
import dayjs from "dayjs";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { formatTerritoryAddress } from "@shared/utils/address";

interface Step1ConsumerSelectionProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}
interface MeterReading {
  meter_id: string;
  meter_number: string;
  current_reading: number;
  reading_date: string;
  reading_source: number;
  status: string;
  product_code: string;
}
// Frequency value to days/months mapping
const FREQUENCY_CALCULATIONS = {
  1: { type: "days", value: 7 }, // Weekly
  3: { type: "days", value: 60 }, // Bi-Monthly
  4: { type: "months", value: 1 }, // Monthly
  5: { type: "days", value: 1 }, // Daily
  6: { type: "months", value: 3 }, // Quarterly
  7: { type: "years", value: 1 }, // Yearly
  8: { type: "months", value: 6 }, // Half-Yearly
};

const Step1ConsumerSelection: React.FC<Step1ConsumerSelectionProps> = ({
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const { id, action = "add" } = useParams();
  const location = useLocation();
  const draftIdFromUrl = new URLSearchParams(location.search).get("draftId");
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const navigate = useNavigate();

  const { data: billTemplates } = useBillTemplates();
  const { data: billingPeriod } = useChoices("billing_period");
  const billingPeriodChoices = mapToLabelValue(billingPeriod);

  const { mutateAsync: submitConsumer, isPending: isSubmitting } =
    useSubmitConsumerSelection();
  const { mutateAsync: updateConsumer } = useUpdateConsumerSelection(
    stepHelpers?.getStepData(0)?.billDraftId || id
  );

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 2000);
  const [selectedConsumer, setSelectedConsumer] = useState<Consumer | null>(null);
  const [shouldAutoSelectConsumer, setShouldAutoSelectConsumer] = useState(false);
  
  // State for tracking form values
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [frequency, setFrequency] = useState<number | null>(null);
  const [billingDuration, setBillingDuration] = useState<number | null>(null);

  const dataLoadedRef = useRef(false);
  const draftLoadedRef = useRef(false);
  const consumerAutoSelectedRef = useRef(false);

  const { data: consumers } = useConsumers(debouncedSearchQuery);
  const { data: billData } = useBillData(id);
  const { data: draftDetails } = useBillDraftDetails(remoteUtilityId, draftIdFromUrl);

  // Define form fields
  const searchFilterFormFields: FormField[] = [
    {
      name: "frequency",
      label: "Billing Period",
      type: "select",
      options: billingPeriodChoices,
      required: true,
      placeholder: "Select Period",
      fullWidth: true,
    },
    {
      name: "startDate",
      label: "Billing Period Start",
      type: "datepicker",
      required: true,
      placeholder: "Select start date",
      fullWidth: true,
    },
    {
      name: "endDate",
      label: "Billing Period End",
      type: "datepicker",
      required: true,
      placeholder: "Select end date",
      fullWidth: true,
    },
    {
      name: "billTemplate",
      label: "Bill Template",
      type: "select",
      required: true,
      placeholder: "Select a template",
      fullWidth: true,
      options: billTemplates?.map((template) => ({
        value: JSON.stringify(template),
        label: template.label,
      })),
    },
  ];

  const searchFilterForm = FormService.createForm({
    fields: searchFilterFormFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Helper functions for step data
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;
      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step 1 data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;
    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  const getAddressField = useCallback((address) => {
    if (address) {
      const serviceAddress = { "service": address };
      return formatTerritoryAddress(serviceAddress, "service");
    }
    return "";
  }, []);

  // Calculate end date based on start date and frequency
  const calculateEndDate = useCallback((startDateValue: string, frequencyValue: number): string => {
    if (!startDateValue || !frequencyValue) return "";

    const calculation = FREQUENCY_CALCULATIONS[frequencyValue];
    if (!calculation) return "";

    const start = dayjs(normalizeDate(startDateValue));
    let end = start;

    if (calculation.type === "days") {
      end = end.add(calculation.value, "day");
    } else if (calculation.type === "months") {
      end = end.add(calculation.value, "month");
    } else if (calculation.type === "years") {
      end = end.add(calculation.value, "year");
    }

    return end.format("YYYY-MM-DD");
  }, []);

  // Calculate billing duration in days
  const calculateBillingDuration = useCallback((
    startDateValue: string,
    endDateValue: string
  ): number => {
    if (!startDateValue || !endDateValue) return 0;

    const start = dayjs(normalizeDate(startDateValue)).startOf("day");
    const end = dayjs(normalizeDate(endDateValue)).startOf("day");

    return Math.max(0, end.diff(start, "day", false));
  }, []);

  // Set billDraftId immediately when draftIdFromUrl exists
  useEffect(() => {
    if (draftIdFromUrl && stepHelpers && !draftLoadedRef.current) {
      stepHelpers.setStepData(0, { billDraftId: draftIdFromUrl });
    }
  }, [draftIdFromUrl, stepHelpers]);

  // Load draft details and populate form
  useEffect(() => {
    if (draftDetails && billTemplates && !draftLoadedRef.current) {
      const billDraftData = draftDetails?.result?.billDraftData;
      const consumerNo = draftDetails?.result?.consumerNo;
      const meterData = draftDetails?.result?.billDraftData?.readingData;
       if (meterData && Array.isArray(meterData)) {
      const transformedReadingData: MeterReading[] = meterData.map((reading) => ({
        meter_id: String(reading.meter), // Transform 'meter' to 'meter_id'
        meter_number: reading.meterNumber,
        current_reading: Number(reading.currentReading),
        reading_date: reading.readingDate ?? normalizeDate(reading.readingDate),
        reading_source: Number(reading.readingSource),
        status: String(reading.status),
        product_code: reading.productCode,
      }));

      // Save transformed data to step 2
      stepHelpers.setStepData(2, {
        reading_data: transformedReadingData,
         payment_settings: {
          outstanding: !billDraftData?.excludeOutstandingBalance,
          late_fees: billDraftData?.includeLateFees,
          credit_balance: billDraftData?.includeCredit,
          installments: billDraftData?.includeInstallments,
        },
      });
    }
       const configId = billDraftData?.billConfigId;
    const matchedTemplate = billTemplates?.find(
      (template) => template.value === configId
    );

    // ✅ FIXED: Create the complete template object that matches the form's expected format
    let billTemplateValue = undefined;
    if (matchedTemplate) {
      // The form expects the entire template object as a JSON string
      billTemplateValue = JSON.stringify(matchedTemplate);
    }


      // Prepare form data from draft
      const formData = {
        frequency: billDraftData?.frequency,
        startDate: billDraftData?.invoiceStartDate,
        endDate: billDraftData?.invoiceEndDate,
        billTemplate: billTemplateValue,
      };

      // Set local state for dates and frequency
      if (formData.startDate) setStartDate(formData.startDate);
      if (formData.endDate) setEndDate(formData.endDate);
      if (formData.frequency) setFrequency(Number(formData.frequency));

      // Populate the form
      FormService.populateForm(searchFilterForm, formData, searchFilterFormFields);

      // Set search query to trigger consumer search
      if (consumerNo) {
        setSearchQuery(consumerNo);
        setShouldAutoSelectConsumer(true);
      }

      draftLoadedRef.current = true;
    }
  }, [draftDetails, billTemplates]);

  // Auto-select consumer and set complete step data for draft continuation
  useEffect(() => {
    if (shouldAutoSelectConsumer && consumers && consumers.length > 0 && billTemplates && !consumerAutoSelectedRef.current) {
      const targetConsumer = consumers[0];
      const formValues = searchFilterForm.getValues();
      const billDraftData = draftDetails?.result?.billDraftData;
      
       const configId = billDraftData?.billConfigId;
    const matchedTemplate = billTemplates?.find(
      (template) => template.value === configId
    );

    // ✅ FIXED: Use the complete matched template object
    const billTemplateData = matchedTemplate || null;

      // Build payload similar to handleContinue
      const payload = {
        remote_utility_id: remoteUtilityId,
        consumer_data: {
          id: targetConsumer.id,
          status: targetConsumer.status,
          consumer_no: targetConsumer.consumerNo,
          first_name: targetConsumer.firstName,
          last_name: targetConsumer.lastName,
          email: targetConsumer.email,
          phone_number: targetConsumer.contactNumber,
          address: getAddressField(targetConsumer?.territoryHierarchy),
        },
        bill_config_id: billTemplateData?.value,
        start_date: formValues.startDate,
        end_date: formValues.endDate,
        premise: targetConsumer?.premiseCode,
        frequency: formValues.frequency,
      };

      // Set complete step data
      saveStepData({
        selectedConsumer: targetConsumer,
        searchQuery: searchQuery,
        formData: formValues,
        billTemplateData,
        payload,
        billDraftId: draftIdFromUrl,
        consumerSelected: true,
        consumerData: {
          id: targetConsumer?.id,
          name: targetConsumer?.consumerName,
          accountNo: targetConsumer?.consumerNo,
          premiseId: targetConsumer.premiseCode,
          status: targetConsumer?.status,
          utilities: targetConsumer?.utilityService,
          totalOutstanding: targetConsumer?.billDetails?.billAmount,
          lastBillDate: targetConsumer?.billDetails?.billDate,
          activateDate: targetConsumer?.activateDate,
        },
        isStepComplete: true,
        completedAt: new Date().toISOString(),
      });

      // Set selected consumer to show form
      setSelectedConsumer(targetConsumer);
      setShouldAutoSelectConsumer(false);
    }
  }, [consumers, shouldAutoSelectConsumer, billTemplates, draftDetails, remoteUtilityId, draftIdFromUrl, getAddressField]);

  // Load saved data on mount (for regular flow, not draft)
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current && !draftIdFromUrl) {
      try {
        const savedStepData = getStepData();

        if (savedStepData) {
          if (savedStepData.formData) {
            const formData = {
              ...(savedStepData.formData.startDate && {
                startDate: savedStepData.formData.startDate,
              }),
              ...(savedStepData.formData.endDate && {
                endDate: savedStepData.formData.endDate,
              }),
              ...(savedStepData.formData.frequency && {
                frequency: savedStepData.formData.frequency,
              }),
              billTemplate: savedStepData.formData.billTemplate,
            };
            FormService.populateForm(searchFilterForm, formData, searchFilterFormFields);

            // Set local state
            if (formData.startDate) setStartDate(formData.startDate);
            if (formData.endDate) setEndDate(formData.endDate);
            if (formData.frequency) setFrequency(Number(formData.frequency));
          }

          if (savedStepData.selectedConsumer) {
            setSelectedConsumer(savedStepData.selectedConsumer);
          }

          if (savedStepData.searchQuery) {
            setSearchQuery(savedStepData.searchQuery);
          }

          dataLoadedRef.current = true;
        } else if (action === "draft" && billData) {
          const formData = {
            startDate: billData?.invoiceStartDate,
            endDate: billData?.invoiceEndDate,
            frequency: billData?.frequency,
            billTemplate: JSON.stringify({
              value: billData?.billConfigId,
              label: billData?.billConfigName,
              source: billData?.billConfigType,
            }),
          };
          FormService.populateForm(searchFilterForm, formData, searchFilterFormFields);

          // Set local state
          if (formData.startDate) setStartDate(formData.startDate);
          if (formData.endDate) setEndDate(formData.endDate);
          if (formData.frequency) setFrequency(Number(formData.frequency));

          setSearchQuery(billData.consumerData.consumerNo);
          setShouldAutoSelectConsumer(true);

          dataLoadedRef.current = true;
        }
      } catch (error) {
        console.error("Failed to load step 1 data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, action, billData, draftIdFromUrl]);

  // Effect to auto-calculate end date when start date or frequency changes
  useEffect(() => {
    if (startDate && frequency && !draftIdFromUrl && dataLoadedRef.current) {
      const calculatedEndDate = calculateEndDate(startDate, frequency);
      if (calculatedEndDate && calculatedEndDate !== endDate) {
        setEndDate(calculatedEndDate);
        searchFilterForm.setValue("endDate", calculatedEndDate);
      }
    }
  }, [startDate, frequency, draftIdFromUrl, calculateEndDate]);

  // Effect to calculate billing duration when dates change
  useEffect(() => {
    if (startDate && endDate) {
      const duration = calculateBillingDuration(startDate, endDate);
      setBillingDuration(duration);
    } else {
      setBillingDuration(null);
    }
  }, [startDate, endDate, calculateBillingDuration]);

  // Handle field changes
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Update local state based on field name
      if (fieldName === "startDate") {
        setStartDate(value);
      } else if (fieldName === "endDate") {
        setEndDate(value);
      } else if (fieldName === "frequency") {
        setFrequency(Number(value));
      }

      // Save to step data
      const currentData = getStepData() || {};
      const updatedData = {
        ...currentData,
        formData: {
          ...currentData.formData,
          [fieldName]: value,
        },
        lastUpdated: new Date().toISOString(),
      };
      saveStepData(updatedData);
    },
    [getStepData, saveStepData]
  );

  // Handle consumer selection
  const handleSelectConsumer = useCallback(
    (consumer: Consumer) => {
      setSelectedConsumer(consumer);

      // Calculate start date (lastBillDate + 1 day) only if not from draft
      let calculatedStartDate = startDate;
      if (!draftIdFromUrl && consumer.billDetails?.billDate) {
        const lastBillDate = dayjs(normalizeDate(consumer.billDetails?.billDate));
        const nextDay = lastBillDate.add(1, "day");
        calculatedStartDate = nextDay.format("YYYY-MM-DD");
        
        // Update form and state with calculated start date
        setStartDate(calculatedStartDate);
        searchFilterForm.setValue("startDate", calculatedStartDate);
      }

      // Save selected consumer data
      saveStepData({
        selectedConsumer: consumer,
        searchQuery: searchQuery,
        formData: {
          ...searchFilterForm.getValues(),
          startDate: calculatedStartDate,
        },
        consumerSelected: true,
        consumerData: {
          id: consumer?.id,
          name: consumer?.consumerName,
          accountNo: consumer?.consumerNo,
          premiseId: consumer.premiseCode,
          status: consumer?.status,
          utilities: consumer?.utilityService,
          totalOutstanding: consumer?.billDetails?.billAmount,
          lastBillDate: consumer?.billDetails?.billDate,
          activateDate: consumer?.activateDate,
        },
      });
    },
    [saveStepData, searchQuery, draftIdFromUrl, startDate]
  );

  // Handle search query change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      const currentData = getStepData() || {};
      saveStepData({
        ...currentData,
        searchQuery: value,
      });
    },
    [getStepData, saveStepData]
  );

  // Build payload
  const buildPayload = useCallback(() => {
    const formValues = searchFilterForm.getValues();

    const rawTemplate = formValues.billTemplate;
    const billTemplateData =
      typeof rawTemplate === "string" ? JSON.parse(rawTemplate) : rawTemplate;

    const payload = {
      remote_utility_id: remoteUtilityId,
      consumer_data: {
        id: selectedConsumer.id,
        status: selectedConsumer.status,
        consumer_no: selectedConsumer.consumerNo,
        first_name: selectedConsumer.firstName,
        last_name: selectedConsumer.lastName,
        email: selectedConsumer.email,
        phone_number: selectedConsumer.contactNumber,
        address: getAddressField(selectedConsumer?.territoryHierarchy),
      },
      bill_config_id: billTemplateData.value,
      bill_config_type: billTemplateData.source,
      start_date: formValues.startDate,
      end_date: formValues.endDate,
      premise: selectedConsumer?.premiseCode,
      frequency: formValues.frequency,
    };

    return { payload, billTemplateData };
  }, [selectedConsumer, remoteUtilityId, getAddressField]);

  const handleContinue = async () => {
    clearValidationError?.();

    const isValid = await FormService.validateForm(searchFilterForm);
    if (!isValid) {
      setValidationError?.("Please fill in all required fields.");
      return;
    }

    if (!selectedConsumer) {
      setValidationError?.("Please select a consumer to continue.");
      return;
    }

    try {
      const { payload, billTemplateData } = buildPayload();
      const existingDraftId = stepHelpers?.getStepData(0)?.billDraftId || id;

      let response;
      let billDraftId = existingDraftId;

      if (existingDraftId) {
        // EDIT FLOW
        response = await updateConsumer({ ...payload });

        if (!response) {
          setValidationError?.("Failed to update bill draft. Please try again.");
          return;
        }
      } else {
        // CREATE FLOW
        response = await submitConsumer(payload);
        billDraftId = response?.draftId;

        if (!billDraftId) {
          setValidationError?.("Failed to create bill draft. Please try again.");
          return;
        }
      }

      saveStepData({
        selectedConsumer,
        formData: searchFilterForm.getValues(),
        billTemplateData,
        payload,
        billDraftId,
        response,
        isStepComplete: true,
        completedAt: new Date().toISOString(),
      });

      onNext?.();
    } catch (error) {
      console.error("Failed to submit consumer selection:", error);
      setValidationError?.("Failed to submit. Please try again.");
    }
  };

  const handleDraft = async () => {
    clearValidationError?.();

    const isValid = await FormService.validateForm(searchFilterForm);
    if (!isValid) {
      setValidationError?.("Please fill in all required fields.");
      return;
    }

    if (!selectedConsumer) {
      setValidationError?.("Please select a consumer to continue.");
      return;
    }

    try {
      const { payload, billTemplateData } = buildPayload();
      let response;
      let billDraftId;

      if (stepHelpers?.getStepData(0)?.billDraftId || id) {
        response = await updateConsumer(payload);
        billDraftId = response?.billDraftId;
      } else {
        response = await submitConsumer(payload);
        billDraftId = response?.billDraftId;
      }

      if (billDraftId) {
        saveStepData({
          selectedConsumer,
          formData: searchFilterForm.getValues(),
          billTemplateData,
          payload,
          billDraftId,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        });

        navigate("/bx/single-bill");
      } else {
        setValidationError?.("Failed to create bill draft. Please try again.");
      }
    } catch (error) {
      console.error("Failed to submit consumer selection:", error);
      setValidationError?.("Failed to submit. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="card-subheading-text">
          Select a consumer and configure billing details to start the bill generation process.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, account no"
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Consumer list panel - LEFT SIDE */}
        <div
          className={cn(
            "transition-all duration-500 ease-in-out",
            selectedConsumer ? "md:col-span-2" : "md:col-span-3"
          )}
        >
          <Card className="h-full pt-0">
            <CardContent className="h-full">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-base font-medium">Consumer List</h3>
                  <p className="card-subheading-text mb-4">Select a consumer from the list below.</p>
                </div>
                <span className="text-xs">{consumers?.length || 0} consumers found</span>
              </div>
              {consumers?.length === 0 ? (
                <div className="flex items-center justify-center h-64 border rounded-md bg-muted/20">
                  <p className="text-muted-foreground">No consumers match your search</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[50vh] overflow-y-scroll">
                  {consumers?.map((consumer) => (
                    <div
                      key={consumer.id}
                      className={cn(
                        "border rounded-md p-4 cursor-pointer transition-all",
                        selectedConsumer?.id === consumer.id
                          ? "border-primary bg-primary/5"
                          : "hover:border-muted-foreground/20"
                      )}
                      onClick={() => handleSelectConsumer(consumer)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="card-heading-text">{consumer?.consumerName}</h4>
                          <div className="text-xs text-gray-500 flex items-center">
                            <Badge variant="secondary">{consumer?.consumerNo}</Badge> |{" "}
                            <MapPin size={14} className="mx-2" />{" "}
                            {getAddressField(consumer?.territoryHierarchy)}
                          </div>
                        </div>
                        <Badge className={getStatusBadgeClass(consumer?.statusDisplay)}>
                          {consumer?.statusDisplay}
                        </Badge>
                      </div>

                      <Separator className="my-2" />

                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <div className="card-subheading-text">Utilities</div>
                          <div className="text-xs font-medium">
                            {consumer?.utilityService.join(", ")}
                          </div>
                        </div>
                        <div>
                          <div className="card-subheading-text">Bill Amount:</div>
                          <div className="text-xs font-medium">
                            {currency} {consumer?.billDetails?.billAmount}
                          </div>
                        </div>
                        <div>
                          <div className="card-subheading-text">Last Bill</div>
                          <div className="text-xs font-medium">
                            {consumer?.billDetails?.billDate || "NA"}
                          </div>
                        </div>
                        <div>
                          <div className="card-subheading-text">Activate Date</div>
                          <div className="text-xs font-medium">{consumer?.activateDate || "NA"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Form panel - RIGHT SIDE - Only show when consumer is selected */}
        {selectedConsumer && (
          <div className="md:col-span-1 space-y-4">
            <Card>
              <CardContent>
                <div className="w-full">
                  <div className="space-y-4">
                    <DynamicForm
                      fields={searchFilterFormFields}
                      form={searchFilterForm}
                      onFieldChange={handleFieldChange}
                      config={{
                        layout: "vertical",
                        spacing: "small",
                        showResetButton: false,
                        showSubmitButton: false,
                        classes: FIELD_CLASSES,
                      }}
                      onSubmit={() => {}}
                    />

                    {/* Billing Duration Display */}
                    {billingDuration !== null && (
                      <div className="mt-4 p-4 border border-blue-500 rounded-md bg-blue-50">
                        <div className="text-sm font-medium mb-1 flex items-center justify-between">
                          <span>Billing Duration</span>
                          <span className="flex gap-1">
                            <Badge variant="outline" className="bg-white">
                              {startDate}
                            </Badge>{" "}
                            -{" "}
                            <Badge variant="outline" className="bg-white">
                              {endDate}
                            </Badge>
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-primary">
                          {billingDuration} {billingDuration === 1 ? "Day" : "Days"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4">
        <Button
          onClick={handleDraft}
          disabled={!selectedConsumer || isSubmitting || isValidating}
          className="min-w-[200px] mr-5"
        >
          Save as Draft
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedConsumer || isSubmitting || isValidating}
          className="min-w-[200px]"
          isLoading={isSubmitting || isValidating}
        >
          Continue to Account Overview
        </Button>
      </div>
    </div>
  );
};

export default Step1ConsumerSelection;